/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { Avatar, PERSON_COLORS, colorClass, colorFor } from "../shared/Avatar";
import { SkillChips } from "./SkillChips";
import type { Member, Role } from "./types";

type Props = {
  member: Member | null;
  roles: Role[];
  canSeeWages: boolean;
  onClose: (changed: boolean) => void;
};

const numOrNull = (v: string) => (v.trim() === "" ? null : Number(v));

// Add or edit someone on the rota: contact, competences, contract hours,
// wage (managers), and an optional link to their POS user for clock-ins.
export const MemberEditor = defineComponent<Props>({
  name: "MemberEditor",
  props: ["member", "roles", "canSeeWages", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const m = props.member;

    const name = ref(m?.name ?? "");
    const email = ref(m?.email ?? "");
    const phone = ref(m?.phone ?? "");
    const color = ref<string | null>(m?.color ?? null);
    const userId = ref<number | null>(m?.user_id ?? null);
    const hours = ref<number | null>(m?.contract_hours_week ?? null);
    const rate = ref<number | null>(m?.hourly_rate ?? null);
    const notes = ref(m?.notes ?? "");
    const active = ref(m?.active ?? true);
    const roleIds = ref<number[]>(m?.role_ids ?? []);
    const posUsers = ref<{ id: number; name: string }[]>([]);
    const saving = ref(false);

    onMounted(async () => {
      try {
        posUsers.value = ((await trpc.staff.list.query()) as { id: number; name: string }[]).map((u) => ({ id: u.id, name: u.name }));
      } catch {
        posUsers.value = [];
      }
    });

    const save = async () => {
      saving.value = true;
      try {
        await trpc.rota.team.save.mutate({
          id: m?.id,
          name: name.value.trim(),
          email: email.value.trim() || null,
          phone: phone.value.trim() || null,
          color: color.value,
          user_id: userId.value,
          contract_hours_week: hours.value,
          hourly_rate: props.canSeeWages ? rate.value : undefined,
          notes: notes.value.trim() || null,
          active: active.value,
          role_ids: roleIds.value,
        });
        toast.showToast(t("workday.common.saved"), { type: "success" });
        props.onClose(true);
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("workday.common.save_failed"), { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    const field = (label: string, input: unknown, hint?: string) => (
      <label class="block">
        <span class="mb-1 block text-sm font-medium">{label}</span>
        {input}
        {hint && <span class="mt-1 block text-xs text-base-content/50">{hint}</span>}
      </label>
    );

    return () => (
      <LemmonModal
        title={m ? t("workday.team.edit_title") : t("workday.team.new_title")}
        onClose={() => props.onClose(false)}
        renderButtons={() => (
          <div class="border-t p-4">
            <div class="flex justify-between gap-2">
              <button class="btn btn-neutral" onClick={() => props.onClose(false)}>
                {t("general.words.cancel")}
              </button>
              <button class="btn btn-primary" disabled={saving.value || !name.value.trim()} onClick={save}>
                {t("workday.common.save")}
              </button>
            </div>
          </div>
        )}
      >
        <div class="space-y-5 p-6">
          <div class="flex items-center gap-4">
            <Avatar name={name.value || "?"} id={m?.id ?? name.value} color={color.value} size="lg" />
            <div class="flex-1">
              {field(t("workday.common.name"), <input class="input input-bordered w-full" v-model={name.value} placeholder="Freja Berg" />)}
            </div>
          </div>
          <div class="flex flex-wrap gap-2" role="radiogroup" aria-label={t("workday.team.color")}>
            {PERSON_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={c}
                class={[
                  "h-7 w-7 rounded-full ring-offset-2 ring-offset-base-100 transition",
                  colorClass(c),
                  colorFor(m?.id ?? name.value, color.value) === c ? "ring-2 ring-base-content" : "",
                ].join(" ")}
                onClick={() => (color.value = c)}
              />
            ))}
          </div>
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {field(
              t("workday.team.email"),
              <input type="email" class="input input-bordered input-sm w-full" v-model={email.value} />,
              t("workday.team.email_hint"),
            )}
            {field(t("workday.team.phone"), <input type="tel" class="input input-bordered input-sm w-full" v-model={phone.value} />)}
          </div>

          <div>
            <div class="mb-2 text-sm font-medium">{t("workday.team.competences")}</div>
            {props.roles.length ? (
              <SkillChips roles={props.roles} selected={roleIds.value} onChange={(ids: number[]) => (roleIds.value = ids)} />
            ) : (
              <p class="text-xs text-base-content/60">{t("workday.team.no_roles")}</p>
            )}
          </div>

          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {field(
              t("workday.team.contract_hours"),
              <div class="join w-full">
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  class="input join-item input-bordered input-sm w-full"
                  value={hours.value ?? ""}
                  onChange={(e: Event) => (hours.value = numOrNull((e.target as HTMLInputElement).value))}
                />
                <span class="join-item flex items-center bg-base-200 px-3 text-xs">{t("workday.team.hours_per_week")}</span>
              </div>,
            )}
            {props.canSeeWages &&
              field(
                t("workday.team.hourly_rate"),
                <input
                  type="number"
                  min="0"
                  step="0.05"
                  class="input input-bordered input-sm w-full"
                  value={rate.value ?? ""}
                  onChange={(e: Event) => (rate.value = numOrNull((e.target as HTMLInputElement).value))}
                />,
                t("workday.team.rate_hint"),
              )}
          </div>

          {field(
            t("workday.team.pos_user"),
            <select
              class="select select-bordered select-sm w-full"
              value={userId.value ?? ""}
              onChange={(e: Event) => {
                const v = (e.target as HTMLSelectElement).value;
                userId.value = v ? Number(v) : null;
              }}
            >
              <option value="">{t("workday.team.no_pos_user")}</option>
              {posUsers.value.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>,
            t("workday.team.pos_user_hint"),
          )}
          {field(t("workday.team.notes"), <textarea class="textarea textarea-bordered w-full text-sm" rows={2} v-model={notes.value} />)}
          {m && (
            <label class="flex items-center gap-2 text-sm">
              <input type="checkbox" class="toggle toggle-sm" v-model={active.value} />
              {t("workday.team.active_help")}
            </label>
          )}
        </div>
      </LemmonModal>
    );
  },
});
